import { useState } from 'react';
import { Dialog, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DialogContentSafeArea } from '@/components/ui/dialog-safe-area';
import { Button } from '@/components/ui/button';
import { Copy, ExternalLink, Mail, MessageSquare } from 'lucide-react';
import { KontentinoPromoCard } from '../../content-calendar/components/KontentinoPromoCard';
import { useOpenAIActions } from 'react-openai-apps-sdk';

interface ExportBrandProfileDialogProps {
  open: boolean;
  onClose: () => void;
  uploadedUrl: string | null;
  filename: string | null;
  exportType: 'pdf' | 'image';
  onSendViaEmail: () => void;
}

export function ExportBrandProfileDialog({
  open,
  onClose,
  uploadedUrl,
  filename,
  exportType,
  onSendViaEmail,
}: ExportBrandProfileDialogProps) {
  const [copiedMessage, setCopiedMessage] = useState<string | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const { sendFollowUpMessage } = useOpenAIActions();

  const getTitle = () => {
    return exportType === 'pdf'
      ? 'Your Brand Guidelines PDF is Ready!'
      : 'Your Brand Profile Image is Ready!';
  };

  const getDescription = () => {
    return exportType === 'pdf'
      ? 'Choose how you\'d like to share your brand guidelines document'
      : 'Choose how you\'d like to share your brand profile image';
  };

  const handleShareInChat = async () => {
    if (!uploadedUrl || !filename) return;

    const fileType = exportType === 'pdf' ? 'brand guidelines PDF' : 'brand profile image';
    const message = `✅ Here's the exported ${fileType}:\n\n[Download ${filename}](${uploadedUrl})`;

    await sendFollowUpMessage({
      prompt: message,
      fallbackMessage: 'ChatGPT API not available. Try copying the URL instead.',
      onSuccess: () => {
        setCopiedMessage('Shared in chat!');
        setTimeout(() => {
          setCopiedMessage(null);
          onClose();
        }, 1500);
      },
      onError: (error) => {
        console.error('Share in chat failed:', error);
        setCopiedMessage('Failed to share. Try copying the URL instead.');
        setTimeout(() => setCopiedMessage(null), 3000);
      },
    });
  };

  const handleOpenInBrowser = () => {
    if (!uploadedUrl) return;
    const newWindow = window.open(uploadedUrl, '_blank');
    if (!newWindow) {
      console.error('Popup blocked - enable popups');
      setCopiedMessage('Popup blocked. Please enable popups to open the file.');
      setTimeout(() => setCopiedMessage(null), 3000);
    } else {
      setCopiedMessage('Opening file...');
      setTimeout(() => setCopiedMessage(null), 2000);
    }
  };

  const handleCopyUrl = () => {
    if (!uploadedUrl) return;
    navigator.clipboard.writeText(uploadedUrl);
    setCopiedMessage('Link copied!');
    setTimeout(() => setCopiedMessage(null), 2000);
  };

  if (!uploadedUrl) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContentSafeArea className="!max-w-md">
        <DialogHeader>
          <DialogTitle>{getTitle()}</DialogTitle>
          <DialogDescription>{getDescription()}</DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <div className="space-y-3">
            <Button
              onClick={() => {
                onSendViaEmail();
                setCopiedMessage('Opening email...');
                setTimeout(() => setCopiedMessage(null), 2000);
              }}
              variant="default"
              size="lg"
              className="w-full justify-start gap-3"
            >
              <Mail className="h-5 w-5" />
              <span>Send via Email</span>
            </Button>
            <Button
              onClick={handleShareInChat}
              variant="outline"
              size="lg"
              className="w-full justify-start gap-3"
            >
              <MessageSquare className="h-5 w-5" />
              <span>Share with ChatGPT</span>
            </Button>
            <Button
              onClick={handleOpenInBrowser}
              variant="outline"
              size="lg"
              className="w-full justify-start gap-3"
            >
              <ExternalLink className="h-5 w-5" />
              <span>Open in Browser</span>
            </Button>

            <div className="space-y-2">
              <Button
                variant="ghost"
                size="sm"
                className="w-full text-sm text-muted-foreground hover:text-foreground"
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                {showAdvanced ? '▼' : '▶'} Having trouble? Get direct link
              </Button>
              {showAdvanced && (
                <div className="space-y-2 pt-2">
                  <p className="text-xs text-muted-foreground">
                    Copy this URL and paste it into a new browser tab:
                  </p>
                  <div className="relative">
                    <div className="text-xs bg-muted p-3 rounded-md font-mono break-all border">
                      {uploadedUrl}
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="absolute top-1 right-1"
                      onClick={handleCopyUrl}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <KontentinoPromoCard className="mt-6" />
        </div>

        {copiedMessage && (
          <div className="flex items-center justify-center gap-2 text-sm font-medium bg-emerald-50/50 dark:bg-emerald-950/20 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800 p-3 rounded-md">
            <span className="text-lg">✓</span>
            <span>{copiedMessage}</span>
          </div>
        )}
      </DialogContentSafeArea>
    </Dialog>
  );
}
